import {
  MESSAGE_PAGINATION_DEFAULTS,
  CONVERSATION_PAGE_LIMIT,
  DEFAULT_MESSAGE_LIMIT,
  buildAssistantMessageFromJobResult,
  buildConversationFromJobResult,
  createMessagePagination,
  handleConversationLevelError,
} from './helpers/conversationHelpers';

const state = {
  conversations: {},
  messages: {},
  pagination: {
    totalCount: 0,
    limit: CONVERSATION_PAGE_LIMIT,
    offset: 0,
  },
  messagePagination: {},
  tempIdCounter: 0,
  jobIdToTempId: {},
};

const getters = {
  conversations: state => state.conversations,
  all: state => Object.values(state.conversations),
  get: state => id => state.conversations[id],
  pagination: state => state.pagination,
  messages: state => conversationId => state.messages[conversationId] || [],
  messagePagination: state => conversationId => state.messagePagination[conversationId] || MESSAGE_PAGINATION_DEFAULTS,
};

const mutations = {
  setConversations(state, { conversations, totalCount, limit, offset }) {
    conversations.forEach(conversation => {
      state.conversations[conversation.id] = conversation;
    });
    state.pagination.totalCount = totalCount;
    state.pagination.limit = limit;
    state.pagination.offset = offset;
  },
  setConversation(state, conversation) {
    state.conversations[conversation.id] = conversation;
  },
  setMessages(state, { conversationId, messages, totalMessages, limit, offset }) {
    state.messages[conversationId] = messages;
    state.messagePagination[conversationId] = {
      totalMessages,
      limit,
      offset,
    };
  },
  clearMessages(state, conversationId) {
    delete state.messages[conversationId];
    delete state.messagePagination[conversationId];
  },
  createTemporaryConversation(state, { jobId, userMessage, aiEventId, assetId, assetType }) {
    const now = new Date().toISOString();
    const conversation = {
      id: jobId,
      title: userMessage.substring(0, 50),
      aiEventId: aiEventId,
      assetId: assetId,
      assetType: assetType,
      lastMessageAt: now,
      createdAt: now,
      metadata: {},
      isTemporary: true,
    };
    state.conversations[jobId] = conversation;

    const message = {
      id: null,
      role: 'user',
      content: userMessage,
      createdAt: now,
      metadata: {},
    };
    state.messages[jobId] = [message];
    state.messagePagination[jobId] = createMessagePagination(1);
  },
  linkTempIdToJobId(state, { tempId, jobId }) {
    state.jobIdToTempId[jobId] = tempId;
  },
  unlinkJobId(state, jobId) {
    delete state.jobIdToTempId[jobId];
  },
  replaceTemporaryConversation(state, { jobId, conversation, assistantMessage }) {
    delete state.conversations[jobId];

    const userMessages = state.messages[jobId] || [];
    delete state.messages[jobId];
    delete state.messagePagination[jobId];

    state.conversations[conversation.id] = conversation;

    const messages = [...userMessages, assistantMessage];
    state.messages[conversation.id] = messages;
    state.messagePagination[conversation.id] = createMessagePagination(messages.length);
  },
  addMessage(state, { conversationId, message }) {
    const existing = state.messages[conversationId] || [];
    state.messages[conversationId] = [...existing, message];

    const pagination = state.messagePagination[conversationId];
    if (pagination) {
      state.messagePagination[conversationId] = {
        ...pagination,
        totalMessages: pagination.totalMessages + 1,
      };
    }
  },
  markLastMessageAsFailed(state, { conversationId, error }) {
    const messages = state.messages[conversationId];
    if (messages && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      messages[messages.length - 1] = {
        ...lastMessage,
        hasError: true,
        errorMessage: error || 'Failed to send message',
      };
    }
  },
  markConversationAsFailed(state, { conversationId, error }) {
    const conversation = state.conversations[conversationId];
    if (conversation && conversation.isTemporary) {
      state.conversations[conversationId] = {
        ...conversation,
        hasError: true,
        errorMessage: error || 'Failed to create conversation',
      };
    }
  },
  markConversationAsTimedOut(state, conversationId) {
    const conversation = state.conversations[conversationId];
    if (conversation && conversation.isTemporary) {
      state.conversations[conversationId] = {
        ...conversation,
        hasTimedOut: true,
      };
    }
  },
  setMessageFeedback(state, { conversationId, messageId, feedback }) {
    const messages = state.messages[conversationId];
    if (messages) {
      const messageIndex = messages.findIndex(msg => msg.id === messageId);
      if (messageIndex !== -1) {
        messages[messageIndex] = {
          ...messages[messageIndex],
          feedback,
        };
      }
    }
  },
  incrementTempIdCounter(state) {
    state.tempIdCounter += 1;
  },
};

const actions = {
  getNextTempId({ commit, state }) {
    commit('incrementTempIdCounter');
    return `temp-${state.tempIdCounter}`;
  },
  fetchConversations({ commit, rootGetters }, { limit = CONVERSATION_PAGE_LIMIT, offset = 0 } = {}) {
    const params = { limit, offset };
    return rootGetters['session/ajax'].get('/conversations', { params }).then(response => {
      commit('setConversations', {
        conversations: response.data.conversations,
        totalCount: response.data.totalCount,
        limit: response.data.limit,
        offset: response.data.offset,
      });
      return response.data;
    });
  },
  fetchConversation({ commit, rootGetters }, { id, messageLimit = DEFAULT_MESSAGE_LIMIT, messageOffset = 0 }) {
    const params = {
      messageLimit: messageLimit,
      messageOffset: messageOffset,
    };
    return rootGetters['session/ajax'].get(`/conversations/${id}`, { params }).then(response => {
      commit('setConversation', response.data.conversation);
      commit('setMessages', {
        conversationId: id,
        messages: response.data.messages,
        totalMessages: response.data.totalMessages,
        limit: response.data.messageLimit,
        offset: response.data.messageOffset,
      });
      return response.data;
    });
  },
  clearConversationMessages({ commit }, conversationId) {
    commit('clearMessages', conversationId);
  },
  createTemporaryConversation({ commit }, { jobId, userMessage, aiEventId, assetId, assetType }) {
    commit('createTemporaryConversation', { jobId, userMessage, aiEventId, assetId, assetType });
    return jobId;
  },
  linkTempIdToJobId({ commit }, { tempId, jobId }) {
    commit('linkTempIdToJobId', { tempId, jobId });
  },
  async resolveTemporaryConversation({ commit, dispatch, state }, { jobId, jobResult }) {
    const tempId = state.jobIdToTempId[jobId];
    if (!tempId) {
      console.error('No temp ID found for job ID:', jobId);
      return;
    }

    const conversationId = jobResult.result?.conversationId;
    if (!conversationId) {
      console.error('No conversationId found in job result');
      return;
    }

    const assistantMessage = buildAssistantMessageFromJobResult(jobResult);
    if (!assistantMessage) {
      console.error('No message output found in job result');
      return;
    }

    const tempConversation = state.conversations[tempId];

    const conversation = buildConversationFromJobResult(jobResult, tempConversation);

    commit('replaceTemporaryConversation', {
      jobId: tempId,
      conversation,
      assistantMessage,
    });

    commit('unlinkJobId', jobId);

    dispatch('mira/setConversationId', conversationId, { root: true });
    dispatch('mira/setPolling', false, { root: true });
    await dispatch('fetchConversation', { id: conversationId });

    return conversationId;
  },
  markConversationAsFailed({ commit, dispatch, state }, { conversationId, error }) {
    handleConversationLevelError(
      { commit, dispatch, state },
      conversationId,
      (tempId) => commit('markConversationAsFailed', { conversationId: tempId, error }),
    );
  },
  markLastMessageAsFailed({ commit, dispatch }, { conversationId, error }) {
    commit('markLastMessageAsFailed', { conversationId, error });
    dispatch('mira/setPolling', false, { root: true });
  },
  markConversationAsTimedOut({ commit, dispatch, state }, conversationId) {
    handleConversationLevelError(
      { commit, dispatch, state },
      conversationId,
      (tempId) => commit('markConversationAsTimedOut', tempId),
    );
  },
  addAssistantMessageFromJob({ commit }, { conversationId, jobResult }) {
    const assistantMessage = buildAssistantMessageFromJobResult(jobResult);
    if (!assistantMessage) {
      console.error('No message output found in job result');
      return;
    }

    commit('addMessage', { conversationId, message: assistantMessage });
  },
  submitMessageFeedback({ commit, rootGetters }, { conversationId, messageId, feedback }) {
    const data = { feedback };
    return rootGetters['session/ajax']
      .patch(`/conversations/${conversationId}/messages/${messageId}/feedback`, data)
      .then(response => {
        commit('setMessageFeedback', { conversationId, messageId, feedback });
        return response.data;
      });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
